package com.fantasytenis.shared.core.realtime

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.json.Json
import kotlin.coroutines.resume

/**
 * JavaScript/Web implementation of RealtimeConnection using Socket.IO.
 *
 * Uses external Socket.IO client library loaded via script tag.
 */
class JsRealtimeConnection(
    private val serverUrl: String
) : RealtimeConnection {

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    private var socket: dynamic = null

    private val _connectionState = MutableStateFlow(ConnectionState.DISCONNECTED)
    override val connectionState: StateFlow<ConnectionState> = _connectionState.asStateFlow()

    override val isConnected: Boolean
        get() = _connectionState.value == ConnectionState.CONNECTED

    private val liveScoreListeners = mutableListOf<(LiveScorePayload) -> Unit>()
    private val contestLeaderboardListeners = mutableListOf<(ContestLeaderboardPayload) -> Unit>()
    private val bracketLeaderboardListeners = mutableListOf<(BracketLeaderboardPayload) -> Unit>()
    private val survivorLeaderboardListeners = mutableListOf<(SurvivorLeaderboardPayload) -> Unit>()
    private val survivorPickResultListeners = mutableListOf<(SurvivorPickResultPayload) -> Unit>()
    private val survivorEliminationListeners = mutableListOf<(SurvivorEliminationPayload) -> Unit>()
    private val userEntryUpdateListeners = mutableListOf<(UserEntryUpdatePayload) -> Unit>()
    private val announcementListeners = mutableListOf<(AnnouncementPayload) -> Unit>()
    private val maintenanceListeners = mutableListOf<(MaintenancePayload) -> Unit>()
    private val connectionStateListeners = mutableListOf<(ConnectionState) -> Unit>()

    override suspend fun connect(token: String?) {
        if (socket != null && isConnected) {
            return
        }

        _connectionState.value = ConnectionState.CONNECTING
        notifyConnectionStateChange(ConnectionState.CONNECTING)

        try {
            // Create Socket.IO connection options
            val options = js("{}")
            options.transports = arrayOf("websocket", "polling")
            options.reconnection = true
            options.reconnectionAttempts = 5
            options.reconnectionDelay = 1000

            // Add auth token if provided
            if (token != null) {
                options.auth = js("{}")
                options.auth.token = token
            }

            // Connect using global io() from socket.io-client
            socket = js("io")(serverUrl, options)

            // Set up event listeners
            setupEventListeners()

        } catch (e: Exception) {
            _connectionState.value = ConnectionState.ERROR
            notifyConnectionStateChange(ConnectionState.ERROR)
            console.error("Failed to connect to realtime server: ${e.message}")
        }
    }

    private fun setupEventListeners() {
        socket.on("connect") {
            console.log("Connected to realtime server")
            _connectionState.value = ConnectionState.CONNECTED
            notifyConnectionStateChange(ConnectionState.CONNECTED)
        }

        socket.on("disconnect") { reason: String ->
            console.log("Disconnected from realtime server: $reason")
            _connectionState.value = ConnectionState.DISCONNECTED
            notifyConnectionStateChange(ConnectionState.DISCONNECTED)
        }

        socket.on("connect_error") { error: dynamic ->
            console.error("Connection error: ${error.message}")
            _connectionState.value = ConnectionState.ERROR
            notifyConnectionStateChange(ConnectionState.ERROR)
        }

        socket.on("reconnecting") {
            console.log("Reconnecting to realtime server...")
            _connectionState.value = ConnectionState.RECONNECTING
            notifyConnectionStateChange(ConnectionState.RECONNECTING)
        }

        socket.on("reconnect") {
            console.log("Reconnected to realtime server")
            _connectionState.value = ConnectionState.CONNECTED
            notifyConnectionStateChange(ConnectionState.CONNECTED)
        }

        // Match events
        socket.on(EventTypes.MATCH_POINT_SCORED) { data: dynamic ->
            handleLiveScoreEvent(data)
        }

        socket.on(EventTypes.MATCH_GAME_COMPLETED) { data: dynamic ->
            handleLiveScoreEvent(data)
        }

        socket.on(EventTypes.MATCH_SET_COMPLETED) { data: dynamic ->
            handleLiveScoreEvent(data)
        }

        socket.on(EventTypes.MATCH_COMPLETED) { data: dynamic ->
            handleLiveScoreEvent(data)
        }

        // Contest events
        socket.on(EventTypes.CONTEST_LEADERBOARD_UPDATE) { data: dynamic ->
            handleContestLeaderboardEvent(data)
        }

        socket.on(EventTypes.CONTEST_ENTRY_UPDATE) { data: dynamic ->
            handleUserEntryUpdateEvent(data)
        }

        // Bracket events
        socket.on(EventTypes.BRACKET_LEADERBOARD_UPDATE) { data: dynamic ->
            handleBracketLeaderboardEvent(data)
        }

        // Survivor events
        socket.on(EventTypes.SURVIVOR_LEADERBOARD_UPDATE) { data: dynamic ->
            handleSurvivorLeaderboardEvent(data)
        }

        socket.on(EventTypes.SURVIVOR_PICK_RESULT) { data: dynamic ->
            handleSurvivorPickResultEvent(data)
        }

        socket.on(EventTypes.SURVIVOR_ELIMINATION) { data: dynamic ->
            handleSurvivorEliminationEvent(data)
        }

        // System events
        socket.on(EventTypes.SYSTEM_ANNOUNCEMENT) { data: dynamic ->
            handleAnnouncementEvent(data)
        }

        socket.on(EventTypes.MAINTENANCE_NOTICE) { data: dynamic ->
            handleMaintenanceEvent(data)
        }
    }

    private fun handleLiveScoreEvent(data: dynamic) {
        try {
            val payload = data.payload

            // Parse individual set scores
            val setScoresArray = payload.setScores as? Array<dynamic> ?: emptyArray<dynamic>()
            val setScores = setScoresArray.map { score ->
                SetScorePayload(
                    homeScore = (score.homeScore as? Number)?.toInt() ?: 0,
                    awayScore = (score.awayScore as? Number)?.toInt() ?: 0,
                    setNumber = (score.setNumber as? Number)?.toInt() ?: 1
                )
            }

            val liveScore = LiveScorePayload(
                matchId = payload.matchId as String,
                player1Id = payload.player1Id as String,
                player1Name = payload.player1Name as String,
                player2Id = payload.player2Id as String,
                player2Name = payload.player2Name as String,
                setScore = payload.setScore as String,
                gameScore = payload.gameScore as String,
                pointScore = payload.pointScore as String,
                isTiebreak = payload.isTiebreak as? Boolean ?: false,
                tiebreakScore = payload.tiebreakScore as? String,
                player1Aces = (payload.player1Aces as? Number)?.toInt() ?: 0,
                player2Aces = (payload.player2Aces as? Number)?.toInt() ?: 0,
                player1DoubleFaults = (payload.player1DoubleFaults as? Number)?.toInt() ?: 0,
                player2DoubleFaults = (payload.player2DoubleFaults as? Number)?.toInt() ?: 0,
                player1Winners = (payload.player1Winners as? Number)?.toInt() ?: 0,
                player2Winners = (payload.player2Winners as? Number)?.toInt() ?: 0,
                player1UnforcedErrors = (payload.player1UnforcedErrors as? Number)?.toInt() ?: 0,
                player2UnforcedErrors = (payload.player2UnforcedErrors as? Number)?.toInt() ?: 0,
                player1BreakPointsWon = (payload.player1BreakPointsWon as? Number)?.toInt() ?: 0,
                player2BreakPointsWon = (payload.player2BreakPointsWon as? Number)?.toInt() ?: 0,
                player1BreakPointsSaved = (payload.player1BreakPointsSaved as? Number)?.toInt() ?: 0,
                player2BreakPointsSaved = (payload.player2BreakPointsSaved as? Number)?.toInt() ?: 0,
                serverId = payload.serverId as? String ?: "",
                status = payload.status as? String ?: "live",
                winnerId = payload.winnerId as? String,
                finalScore = payload.finalScore as? String,
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis(),
                setScores = setScores
            )
            liveScoreListeners.forEach { it(liveScore) }
        } catch (e: Exception) {
            console.error("Error parsing live score event: ${e.message}")
        }
    }

    private fun handleContestLeaderboardEvent(data: dynamic) {
        try {
            val payload = data.payload
            val entries = (payload.entries as Array<dynamic>).map { entry ->
                ContestLeaderboardEntryPayload(
                    entryId = entry.entryId as String,
                    userId = entry.userId as String,
                    displayName = entry.displayName as String,
                    totalPoints = (entry.totalPoints as Number).toDouble(),
                    rank = (entry.rank as Number).toInt(),
                    previousRank = (entry.previousRank as? Number)?.toInt() ?: 0,
                    payoutAmount = (entry.payoutAmount as? Number)?.toDouble() ?: 0.0
                )
            }
            val leaderboard = ContestLeaderboardPayload(
                contestId = payload.contestId as String,
                entries = entries,
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis()
            )
            contestLeaderboardListeners.forEach { it(leaderboard) }
        } catch (e: Exception) {
            console.error("Error parsing contest leaderboard event: ${e.message}")
        }
    }

    private fun handleBracketLeaderboardEvent(data: dynamic) {
        try {
            val payload = data.payload
            val entries = (payload.entries as Array<dynamic>).map { entry ->
                BracketLeaderboardEntryPayload(
                    entryId = entry.entryId as String,
                    userId = entry.userId as String,
                    displayName = entry.displayName as String,
                    bracketName = entry.bracketName as? String ?: "",
                    totalPoints = (entry.totalPoints as Number).toDouble(),
                    correctPicks = (entry.correctPicks as Number).toInt(),
                    pendingPicks = (entry.pendingPicks as Number).toInt(),
                    possiblePoints = (entry.possiblePoints as Number).toDouble(),
                    rank = (entry.rank as Number).toInt(),
                    previousRank = (entry.previousRank as? Number)?.toInt() ?: 0
                )
            }
            val leaderboard = BracketLeaderboardPayload(
                challengeId = payload.challengeId as String,
                entries = entries,
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis()
            )
            bracketLeaderboardListeners.forEach { it(leaderboard) }
        } catch (e: Exception) {
            console.error("Error parsing bracket leaderboard event: ${e.message}")
        }
    }

    private fun handleSurvivorLeaderboardEvent(data: dynamic) {
        try {
            val payload = data.payload
            val entries = (payload.entries as Array<dynamic>).map { entry ->
                SurvivorLeaderboardEntryPayload(
                    entryId = entry.entryId as String,
                    userId = entry.userId as String,
                    displayName = entry.displayName as String,
                    status = (entry.status as Number).toInt(),
                    strikesUsed = (entry.strikesUsed as Number).toInt(),
                    survivedRounds = (entry.survivedRounds as Number).toInt(),
                    lastPickPlayer = entry.lastPickPlayer as? String,
                    lastPickResult = (entry.lastPickResult as? Number)?.toInt()
                )
            }
            val leaderboard = SurvivorLeaderboardPayload(
                contestId = payload.contestId as String,
                aliveCount = (payload.aliveCount as Number).toInt(),
                eliminatedThisRound = (payload.eliminatedThisRound as Number).toInt(),
                entries = entries,
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis()
            )
            survivorLeaderboardListeners.forEach { it(leaderboard) }
        } catch (e: Exception) {
            console.error("Error parsing survivor leaderboard event: ${e.message}")
        }
    }

    private fun handleSurvivorPickResultEvent(data: dynamic) {
        try {
            val payload = data.payload
            val result = SurvivorPickResultPayload(
                contestId = payload.contestId as String,
                entryId = payload.entryId as String,
                pickId = payload.pickId as String,
                playerName = payload.playerName as String,
                result = (payload.result as Number).toInt(),
                matchScore = payload.matchScore as? String,
                isEliminated = payload.isEliminated as Boolean,
                strikesUsed = (payload.strikesUsed as Number).toInt(),
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis()
            )
            survivorPickResultListeners.forEach { it(result) }
        } catch (e: Exception) {
            console.error("Error parsing survivor pick result event: ${e.message}")
        }
    }

    private fun handleSurvivorEliminationEvent(data: dynamic) {
        try {
            val payload = data.payload
            val elimination = SurvivorEliminationPayload(
                contestId = payload.contestId as String,
                entryId = payload.entryId as String,
                userId = payload.userId as String,
                displayName = payload.displayName as String,
                eliminatedInRound = (payload.eliminatedInRound as Number).toInt(),
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis()
            )
            survivorEliminationListeners.forEach { it(elimination) }
        } catch (e: Exception) {
            console.error("Error parsing survivor elimination event: ${e.message}")
        }
    }

    private fun handleUserEntryUpdateEvent(data: dynamic) {
        try {
            val payload = data.payload
            val update = UserEntryUpdatePayload(
                entryId = payload.entryId as String,
                contestId = payload.contestId as String,
                userId = payload.userId as String,
                totalPoints = (payload.totalPoints as Number).toDouble(),
                rank = (payload.rank as Number).toInt(),
                previousRank = (payload.previousRank as? Number)?.toInt() ?: 0,
                status = payload.status as String,
                timestamp = (payload.timestamp as? Number)?.toLong() ?: currentTimeMillis()
            )
            userEntryUpdateListeners.forEach { it(update) }
        } catch (e: Exception) {
            console.error("Error parsing user entry update event: ${e.message}")
        }
    }

    private fun handleAnnouncementEvent(data: dynamic) {
        try {
            val payload = data.payload
            val announcement = AnnouncementPayload(
                message = payload.message as String,
                severity = payload.severity as? String ?: "info"
            )
            announcementListeners.forEach { it(announcement) }
        } catch (e: Exception) {
            console.error("Error parsing announcement event: ${e.message}")
        }
    }

    private fun handleMaintenanceEvent(data: dynamic) {
        try {
            val payload = data.payload
            val maintenance = MaintenancePayload(
                message = payload.message as String,
                scheduledAt = (payload.scheduledAt as? Number)?.toLong(),
                durationMinutes = (payload.durationMinutes as? Number)?.toInt()
            )
            maintenanceListeners.forEach { it(maintenance) }
        } catch (e: Exception) {
            console.error("Error parsing maintenance event: ${e.message}")
        }
    }

    override suspend fun disconnect() {
        socket?.disconnect()
        socket = null
        _connectionState.value = ConnectionState.DISCONNECTED
        notifyConnectionStateChange(ConnectionState.DISCONNECTED)
    }

    override suspend fun subscribeToMatch(matchId: String): Boolean {
        val data = js("({})").unsafeCast<dynamic>()
        data.matchId = matchId
        return emitWithAck("subscribe:match", data)
    }

    override suspend fun unsubscribeFromMatch(matchId: String) {
        val data = js("({})").unsafeCast<dynamic>()
        data.matchId = matchId
        socket?.emit("unsubscribe:match", data)
    }

    override suspend fun subscribeToContest(contestId: String): Boolean {
        val data = js("({})").unsafeCast<dynamic>()
        data.contestId = contestId
        return emitWithAck("subscribe:contest", data)
    }

    override suspend fun unsubscribeFromContest(contestId: String) {
        val data = js("({})").unsafeCast<dynamic>()
        data.contestId = contestId
        socket?.emit("unsubscribe:contest", data)
    }

    override suspend fun subscribeToBracket(challengeId: String): Boolean {
        val data = js("({})").unsafeCast<dynamic>()
        data.challengeId = challengeId
        return emitWithAck("subscribe:bracket", data)
    }

    override suspend fun unsubscribeFromBracket(challengeId: String) {
        val data = js("({})").unsafeCast<dynamic>()
        data.challengeId = challengeId
        socket?.emit("unsubscribe:bracket", data)
    }

    override suspend fun subscribeToSurvivor(contestId: String): Boolean {
        val data = js("({})").unsafeCast<dynamic>()
        data.contestId = contestId
        return emitWithAck("subscribe:survivor", data)
    }

    override suspend fun unsubscribeFromSurvivor(contestId: String) {
        val data = js("({})").unsafeCast<dynamic>()
        data.contestId = contestId
        socket?.emit("unsubscribe:survivor", data)
    }

    override suspend fun getSubscriptions(): List<String> = suspendCancellableCoroutine { cont ->
        if (socket == null) {
            cont.resume(emptyList())
            return@suspendCancellableCoroutine
        }

        socket.emit("subscriptions") { response: dynamic ->
            val subscriptions = (response.subscriptions as? Array<String>)?.toList() ?: emptyList()
            cont.resume(subscriptions)
        }
    }

    private suspend fun emitWithAck(event: String, data: dynamic): Boolean = suspendCancellableCoroutine { cont ->
        if (socket == null) {
            cont.resume(false)
            return@suspendCancellableCoroutine
        }

        socket.emit(event, data) { response: dynamic ->
            val success = response.success as? Boolean ?: false
            cont.resume(success)
        }
    }

    override fun onLiveScore(listener: (LiveScorePayload) -> Unit) {
        liveScoreListeners.add(listener)
    }

    override fun onContestLeaderboard(listener: (ContestLeaderboardPayload) -> Unit) {
        contestLeaderboardListeners.add(listener)
    }

    override fun onBracketLeaderboard(listener: (BracketLeaderboardPayload) -> Unit) {
        bracketLeaderboardListeners.add(listener)
    }

    override fun onSurvivorLeaderboard(listener: (SurvivorLeaderboardPayload) -> Unit) {
        survivorLeaderboardListeners.add(listener)
    }

    override fun onSurvivorPickResult(listener: (SurvivorPickResultPayload) -> Unit) {
        survivorPickResultListeners.add(listener)
    }

    override fun onSurvivorElimination(listener: (SurvivorEliminationPayload) -> Unit) {
        survivorEliminationListeners.add(listener)
    }

    override fun onUserEntryUpdate(listener: (UserEntryUpdatePayload) -> Unit) {
        userEntryUpdateListeners.add(listener)
    }

    override fun onAnnouncement(listener: (AnnouncementPayload) -> Unit) {
        announcementListeners.add(listener)
    }

    override fun onMaintenanceNotice(listener: (MaintenancePayload) -> Unit) {
        maintenanceListeners.add(listener)
    }

    override fun onConnectionStateChange(listener: (ConnectionState) -> Unit) {
        connectionStateListeners.add(listener)
    }

    override fun removeAllListeners() {
        liveScoreListeners.clear()
        contestLeaderboardListeners.clear()
        bracketLeaderboardListeners.clear()
        survivorLeaderboardListeners.clear()
        survivorPickResultListeners.clear()
        survivorEliminationListeners.clear()
        userEntryUpdateListeners.clear()
        announcementListeners.clear()
        maintenanceListeners.clear()
        connectionStateListeners.clear()
    }

    private fun notifyConnectionStateChange(state: ConnectionState) {
        connectionStateListeners.forEach { it(state) }
    }

}

// Helper function to get current time in JS
private fun currentTimeMillis(): Long = js("Date.now()") as Long

// Helper to access console in JS
private external val console: Console

private external interface Console {
    fun log(message: String)
    fun error(message: String)
}
